import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PermissionsSelector from './PermissionsSelector';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { rolesI18n } from './i18n';

const normalizeStatus = (status) => {
  const normalized = String(status || '')
    .trim()
    .toLowerCase();

  if (normalized === 'inactive' || normalized === 'inactivo') {
    return 'inactive';
  }

  return 'active';
};

const RoleCreateEditModal = ({
  DesignSystemComponents,
  show,
  mode = 'create',
  initialData,
  onClose,
  onSave,
}) => {
  const { language } = useLanguage();
  const rolesLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: rolesI18n });

  const { Modal, Input, Dropdown } = DesignSystemComponents;

  const isEdit = mode === 'edit';

  const [form, setForm] = useState({
    name: '',
    description: '',
    status: 'active',
    permissionsTree: [],
  });
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  useEffect(() => {
    rolesI18n.changeLanguage(rolesLanguage);
  }, [rolesLanguage]);

  useEffect(() => {
    if (!show) return;

    setLoadingPermissions(true);

    setForm({
      name: initialData?.name ?? '',
      description: initialData?.description ?? '',
      status: normalizeStatus(initialData?.status),
      permissionsTree: initialData?.permissionsTree ?? [],
    });

    setLoadingPermissions(false);
  }, [show, initialData]);

  const nameValid = useMemo(() => form.name.trim().length > 0, [form.name]);
  const descValid = useMemo(
    () => form.description.trim().length > 0,
    [form.description]
  );

  const hasPermissions = useMemo(() => {
    const walk = (node) => {
      if (node.permission) return true;
      return node.children?.some(walk);
    };

    return form.permissionsTree.some((g) => g.actions?.some(walk));
  }, [form.permissionsTree]);

  const isValid = nameValid && descValid && hasPermissions;

  const setField = (key) => (eventOrValue) => {
    const value = eventOrValue?.target
      ? eventOrValue.target.value
      : eventOrValue;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const collectPermissions = (tree) => {
    const result = [];

    const walk = (node) => {
      if (node.permission) result.push(node.id);
      node.children?.forEach(walk);
    };

    tree.forEach((g) => g.actions?.forEach(walk));
    return result;
  };

  const handleSave = () => {
    setLoadingSave(true);

    const permissionIds = collectPermissions(form.permissionsTree);

    onSave({
      name: form.name.trim(),
      description: form.description || null,
      status: form.status,
      permissionIds,
    }).finally(() => {
      setLoadingSave(false);
    });
  };

  if (!Modal || !Input) return null;

  const statusOptions = [
    { label: t('roles_status_active'), value: 'active' },
    { label: t('roles_status_inactive'), value: 'inactive' },
  ];

  const primaryBtnTxt = loadingSave
    ? 'Cargando...'
    : isEdit
      ? t('roles_modal_primary_edit')
      : t('roles_modal_primary_create');

  return (
    <Modal
      title={
        isEdit ? t('roles_modal_title_edit') : t('roles_modal_title_create')
      }
      show={show}
      onClose={onClose}
      secondaryBtnTxt={t('roles_cancel')}
      primaryBtnTxt={primaryBtnTxt}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid || loadingSave}
      disabledSecondaryBtn={loadingSave}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='960px'
    >
      <div className='row'>
        <div className={`${isEdit ? 'col-6' : 'col-12'} `}>
          <Input
            label={t('roles_modal_name')}
            required
            placeholder={t('roles_modal_name_placeholder')}
            value={form.name}
            onChange={setField('name')}
            error={
              form.name && !nameValid ? t('roles_modal_name_required') : ''
            }
          />
        </div>
        {isEdit && (
          <div className={`${isEdit ? 'col-6' : 'col-12'} `}>
            <Dropdown
              label={t('roles_modal_status')}
              options={statusOptions}
              value={statusOptions.find(
                (option) => option.value === form.status
              )}
              onChange={(option) =>
                setForm((prev) => ({
                  ...prev,
                  status: option?.value || 'active',
                }))
              }
              width='100%'
              placeholder={t('roles_modal_status_placeholder')}
              helperText={
                isEdit && initialData?.users !== 0
                  ? t('roles_modal_status_helper_blocked')
                  : ''
              }
              selectIdName='SEL_change_state'
              disabled={isEdit && initialData?.users !== 0}
            />
          </div>
        )}
        <div className='col-12'>
          <label className='form-label'>
            {t('roles_modal_description')}{' '}
            <span className='text-danger'>*</span>
          </label>
          <textarea
            className='form-control'
            rows={3}
            placeholder={t('roles_modal_description_placeholder')}
            value={form.description}
            onChange={(event) => setField('description')(event)}
            style={{ marginBottom: '20px' }}
          />
          {form.description && !descValid ? (
            <p className='mini text-danger mt-1'>
              {t('roles_modal_description_required')}
            </p>
          ) : null}
        </div>
        <div className='col-12'>
          <p className='caption mb-1'>
            {t('roles_modal_permissions')}{' '}
            <span className='text-danger'>*</span>
          </p>
          {loadingPermissions ? (
            <div className='text-center py-3'>
              <div
                className='spinner-border spinner-border-sm text-primary'
                role='status'
              >
                <span className='visually-hidden'>
                  {t('roles_modal_loading_permissions')}
                </span>
              </div>
              <p className='mt-2 color-gray-600'>
                {t('roles_modal_loading_permissions')}
              </p>
            </div>
          ) : (
            <PermissionsSelector
              tree={form.permissionsTree}
              onChange={(next) =>
                setForm((prev) => ({ ...prev, permissionsTree: next }))
              }
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RoleCreateEditModal;
